import { Button, Space, Tooltip } from 'antd';
import {
  CheckOutlined,
  RollbackOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import s from './ActionsCell.module.scss';

import type { FC } from 'react';
import type { IDashboardTaskRow, TabKey } from 'pages/TasksDashboard/types';

interface IActionsCellProps {
  task: IDashboardTaskRow;
  activeTab: TabKey;
  assigningGuids: Set<string>;
  onAssign: (taskGuid: string) => void;
  completingGuids: Set<string>;
  onComplete: (taskGuid: string) => void;
  onReturn: (taskGuid: string) => void;
  returningGuid: string | null;
}

const ActionsCell: FC<IActionsCellProps> = ({
  activeTab,
  task,
  assigningGuids,
  onAssign,
  completingGuids,
  onComplete,
  onReturn,
  returningGuid,
}) => {
  const { t } = useTranslation();

  if (activeTab === 'completed') return null;

  return (
    <span data-cy="actions-cell">
      <Space>
        {activeTab === 'open_pool' && (
          <Tooltip title={t('tasksDashboard.actions.assignToMe')}>
            <Button
              className={s['assign-btn']}
              icon={<UserAddOutlined />}
              onClick={() => onAssign(task.guid)}
              size="small"
              loading={assigningGuids.has(task.guid)}
              disabled={assigningGuids.has(task.guid)}
              aria-label={t('tasksDashboard.actions.assignToMe')}
              data-cy="assign-to-me-btn"
            />
          </Tooltip>
        )}
        {activeTab === 'assigned_to_me' && (
          <>
            <Tooltip title={t('tasksDashboard.actions.complete')}>
              <Button
                className={s['complete-btn']}
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => onComplete(task.guid)}
                size="small"
                loading={completingGuids.has(task.guid)}
                disabled={completingGuids.has(task.guid)}
                aria-label={t('tasksDashboard.actions.complete')}
                data-cy="complete-task-btn"
              />
            </Tooltip>
            <Tooltip title={t('tasksDashboard.actions.returnToPool')}>
              <Button
                className={s['return-btn']}
                icon={<RollbackOutlined />}
                onClick={() => onReturn(task.guid)}
                size="small"
                disabled={returningGuid === task.guid}
                aria-label={t('tasksDashboard.actions.returnToPool')}
                data-cy="return-to-pool-btn"
              />
            </Tooltip>
          </>
        )}
      </Space>
    </span>
  );
};

export default ActionsCell;
